/**
 * Custom hook for managing Daily.co video consultations
 * Handles room creation, joining, and call state management
 */
import { useState, useCallback, useEffect } from 'react';
import DailyIframe from '@daily-co/daily-js';
import api from '../utils/api';
import { toast } from 'sonner';

export const useVideoConsultation = (appointmentId, patientId) => {
  const [callObject, setCallObject] = useState(null);
  const [roomUrl, setRoomUrl] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isJoined, setIsJoined] = useState(false);
  const [participantRole, setParticipantRole] = useState(null);
  const [localParticipant, setLocalParticipant] = useState(null);
  const [participantIds, setParticipantIds] = useState([]);

  /**
   * Create a new video consultation room
   */
  const createRoom = useCallback(async () => {
    if (!appointmentId || !patientId) {
      toast.error('Missing appointment or patient ID');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const { data } = await api.post('/video/create-room', {
        appointment_id: appointmentId,
        patient_id: patientId
      });

      if (data.success) {
        setRoomUrl(data.room_url);
        setToken(data.doctor_token);
        setParticipantRole('doctor');
        toast.success('Video room created successfully');
        return data;
      }
    } catch (err) {
      let errorMsg = 'Failed to create video room';
      
      // Handle different error formats
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMsg = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            errorMsg = err.response.data.detail.map(e => e.msg || e).join(', ');
          }
        }
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [appointmentId, patientId]);

  /**
   * Join an existing video consultation room
   */
  const joinRoom = useCallback(async (roomName) => {
    if (!roomName) {
      toast.error('Room name is required');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const { data } = await api.post('/video/join-room', {
        room_name: roomName
      });

      if (data.success) {
        setRoomUrl(data.room_url);
        setToken(data.token);
        setParticipantRole(data.role);
        return data;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to join video room';
      setError(errorMsg);
      toast.error(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initialize Daily call object and join the call
   */
  const startCall = useCallback(async () => {
    if (!roomUrl || !token) {
      toast.error('Room URL and token are required');
      return;
    }

    try {
      setLoading(true);

      // Create call object if it doesn't exist
      let daily = callObject;
      if (!daily) {
        daily = DailyIframe.createCallObject({
          url: roomUrl
        });
        setCallObject(daily);

        // Set up event listeners
        daily.on('joined-meeting', (event) => {
          setIsJoined(true);
          setLocalParticipant(event.participants.local);
          setParticipantIds(Object.keys(event.participants));
        });

        daily.on('participant-joined', (event) => {
          setParticipantIds(prevIds => [...prevIds, event.participant.session_id]);
        });

        daily.on('participant-left', (event) => {
          setParticipantIds(prevIds => prevIds.filter(id => id !== event.participant.session_id));
        });

        daily.on('participant-updated', (event) => {
          if (event.participant.local) {
            setLocalParticipant(event.participant);
          }
        });

        daily.on('left-meeting', () => {
          setIsJoined(false);
          setLocalParticipant(null);
          setParticipantIds([]);
        });
      }

      // Join the call
      await daily.join({ url: roomUrl, token: token });
      toast.success('Joined consultation');
    } catch (err) {
      const errorMsg = 'Failed to start call';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [roomUrl, token, callObject]);

  /**
   * Leave the video call
   */
  const leaveCall = useCallback(async () => {
    if (!callObject) return;

    try {
      await callObject.leave();
      setIsJoined(false);
      toast.success('Left consultation');
    } catch (err) {
      toast.error('Error leaving call');
    }
  }, [callObject]);

  /**
   * End the consultation (doctor only)
   */
  const endConsultation = useCallback(async (roomName) => {
    if (!roomName) {
      toast.error('Room name is required');
      return false;
    }

    try {
      await leaveCall();

      const { data } = await api.post('/video/end-consultation', {
        room_name: roomName
      });

      if (data.success) {
        toast.success('Consultation ended');
        
        // Clean up call object
        if (callObject) {
          callObject.destroy();
          setCallObject(null);
        }
        
        return true;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to end consultation';
      toast.error(errorMsg);
      return false;
    }
  }, [callObject, leaveCall]);

  /**
   * Toggle local video
   */
  const toggleVideo = useCallback(async () => {
    if (!callObject) return;

    try {
      const isVideoOn = localParticipant?.video;
      await callObject.setLocalVideo(!isVideoOn);
    } catch (err) {
      toast.error('Failed to toggle video');
    }
  }, [callObject, localParticipant]);

  /**
   * Toggle local audio
   */
  const toggleAudio = useCallback(async () => {
    if (!callObject) return;

    try {
      const isAudioOn = localParticipant?.audio;
      await callObject.setLocalAudio(!isAudioOn);
    } catch (err) {
      toast.error('Failed to toggle audio');
    }
  }, [callObject, localParticipant]);

  /**
   * Start/stop screen sharing
   */
  const toggleScreenShare = useCallback(async () => {
    if (!callObject) return;

    try {
      const participants = callObject.participants();
      const isScreenSharing = participants?.local?.screen;
      
      if (isScreenSharing) {
        await callObject.stopScreenShare();
        toast.success('Screen sharing stopped');
      } else {
        await callObject.startScreenShare();
        toast.success('Screen sharing started');
      }
    } catch (err) {
      toast.error('Failed to toggle screen sharing');
    }
  }, [callObject]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (callObject) {
        callObject.destroy();
      }
    };
  }, [callObject]);

  return {
    // State
    loading,
    error,
    isJoined,
    roomUrl,
    token,
    participantRole,
    callObject,
    localParticipant,
    participantIds,
    
    // Actions
    createRoom,
    joinRoom,
    startCall,
    leaveCall,
    endConsultation,
    toggleVideo,
    toggleAudio,
    toggleScreenShare
  };
};
